import { computed, getCurrentInstance, inject, nextTick, Ref, ref } from "vue";
import { useStore } from "vuex";
import {
  ANCHOR_ADDITIONAL_OFFSET,
  ANNOTATION_CONTEXT_TYPE,
  ANNOTATION_MODE,
  ANNOTATION_POPUP_EVENT,
  ANNOTATION_RESOURCE_TYPE,
  ANNOTATION_TYPE,
  DRAFT_ANNOTATION_UUID,
  NAMESPACE,
  OWNER_TYPE,
} from "@modules/annotation/service/annotation.constant";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";
import DOMUtils from "@utils/dom.utils";
import ScrollUtils from "@utils/scroll.utils";
import DeviceUtils from "@utils/device.utils";
import { isDefined, isEmpty } from "@utils/var.check";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { useAnnotationAnchor } from "@modules/annotation/composables/annotation.anchor.ts";
import { parseOwningComponentIdFromAnchor } from "@modules/annotation/service/annotation.anchor.utils.ts";
import { useAnnotationAttemptAdapter } from "@modules/annotation/composables/use-annotation-attempt-adapter.ts";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import AnnotationListItemContentSubpage from "@modules/annotation/components/AnnotationListItemContentSubpage.vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { AnnotationContext } from "@modules/annotation/types.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { shouldUseInlineAnnotationEditor } from "@modules/annotation/service/annotation.editor.utils";

interface Options {
  deactiveNoteToggle?: () => void;
  getMentionableTeacherList?: () => void;
  getMentionableStudentList?: () => void;
  handleNoteChangeConfirm?: (data: never) => void;
  handleNoteChangeCancel?: () => void;
  getContext?: () => void;
  mode: string;
  includeBuddyListMentions?: boolean;
  isInNotebook?: boolean;
}

interface ActivityNoteOptions {
  visibleByStudents?: boolean;
  mentionModel?: Record<string, never>;
}

interface HandleEditNotesOptions {
  initialOptionType?: string;
  isReposition?: boolean;
  isTranscriptEnabled?: boolean;
}

interface HighlightData {
  highlighterSelectors: { start: number }[];
}

interface AnnotationContent {
  pronunciation?: string;
  definitions: Definition[];
}

interface Definition {
  definition?: string;
  example?: string;
}

// interface EditNoteOptions {
//   isReposition?: boolean;
//   initialOptionType?: string;
//   mode: string;
// }

export function useAnnotationNote(options: Options, dirtyId: Ref<string>) {
  const { deactiveNoteToggle, getMentionableTeacherList, getMentionableStudentList } = options;
  const mode = ref(options.mode);
  const includeBuddyListMentions = ref(options.includeBuddyListMentions);
  const isInNotebook = options.isInNotebook ?? false;

  const annotationContext = inject<AnnotationContext>("annotationContext");
  const richTextContext = inject<unknown>("richTextContext");

  const eventBusWatch = useEventBusWatch();
  const annotationAnchor = useAnnotationAnchor();
  const contentSubpage = getCurrentInstance()?.appContext.config.globalProperties.$contentSubpage;
  const attemptAdapter = useAnnotationAttemptAdapter();
  const { isOwned, isCommentTogglingSupported } = useAnnotationPermissions();

  const canToggleComment = isCommentTogglingSupported.value;

  const store = useStore();
  const route = useRoute();
  const { t } = useI18n();
  const { removeDirty } = useDirty(dirtyId);

  let saveNoteUnsubscribe: (() => void) | null = null;
  let cancelNoteUnsubscribe: (() => void) | null = null;

  const selectedAnnotation = computed(() => store.state[NAMESPACE.ANNOTATION].selectedAnnotation);
  const lastSelectedAnnotation = computed(
    () => store.state[NAMESPACE.ANNOTATION].lastSelectedAnnotation,
  );
  const currentUserId = attemptAdapter.currentUserId.value;
  const userId = store.getters[`${NAMESPACE.AUTH}/userId`];
  const isStudent = store.getters[`${NAMESPACE.AUTH}/isStudent`];

  const getIntegratorAppAccessByAppFeatureId =
    store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`];
  const isActivityTeamResponse = store.getters[`${NAMESPACE.RESOURCE}/isActivityTeamResponse`];
  const userFormattedName = computed(() => store.getters[`${NAMESPACE.AUTH}/userFormattedName`]);
  const isOwnView = computed(() => userId === currentUserId);

  const setSelectedAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/setSelectedAnnotation`, payload);
  const setWithNote = (payload) => store.dispatch(`${NAMESPACE.ANNOTATION}/setWithNote`, payload);
  const persistDraftAnnotation = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/persistDraftAnnotation`, payload);
  const updateAnnotationContent = (payload) =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/updateAnnotationContent`, payload);
  const deleteDraftAnnotation = () =>
    store.dispatch(`${NAMESPACE.ANNOTATION}/deleteDraftAnnotation`);

  const getNoteOwnerFlags = (annotation: unknown) => {
    const canManageNote = isOwned(annotation);

    return {
      canEdit: canManageNote,
      canDelete: canManageNote,
    };
  };

  const viewAnnotationNote = async (params) => {
    await nextTick();
    contentSubpage.show(AnnotationListItemContentSubpage, {
      ...params,
      annotationContext: annotationContext,
      richTextContext: richTextContext,
    });
  };

  const handleEditNotes = async (
    annotationType,
    question,
    options: HandleEditNotesOptions = {},
  ) => {
    if (annotationType !== ANNOTATION_TYPE.ACTIVITY_NOTE) {
      await store.dispatch(
        `${NAMESPACE.ANNOTATION}/setAnchoringAnnotation`,
        selectedAnnotation.value.annotation,
      );
    }
    if (
      shouldUseInlineAnnotationEditor(
        selectedAnnotation.value.annotation,
        options.initialOptionType,
        route.path.includes("/notebook/"),
      )
    ) {
      const annotation = selectedAnnotation.value.annotation;
      if (!annotation) return;
      unbindAnnotationPopupEvent();
      contentSubpage.removeAll();
      annotationAnchor.handleExpandDrawer({
        drawerContentType: annotationContext?.drawerContentType,
      });
      await nextTick();
      if (selectedAnnotation.value.annotation !== annotation) return;
      AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_EDIT_IN_DRAWER, {
        annotationUuid: annotation?.uuid,
        question,
        initialOptionType: options.initialOptionType ?? "",
      });
      setWithNote(false);
      return;
    }
    nextTick(() => {
      const annotation = selectedAnnotation.value.annotation;
      const noteOwnerFlags = getNoteOwnerFlags(annotation);
      bindAnnotationPopupEvent();
      viewAnnotationNote({
        question: question,
        annotationUuid: annotation?.uuid,
        features: {
          comment: annotationContext?.features?.comment,
        },
        autoFocus: true,
        baseTitle: [t("common_assignment_info_title")],
        canComment: true,
        canDeleteComment: true,
        canEdit: noteOwnerFlags.canEdit,
        canDelete: noteOwnerFlags.canDelete,
        canToggleComment,
        showOverflowMenu: true,
        numberOfComments: 0,
        tooltipData: annotation?.data?.tooltipData,
        visibleByStudents: annotation?.visibleByStudents,
        getMentionableStudentList: getMentionableStudentList,
        getMentionableTeacherList: getMentionableTeacherList,
        viewedUserId: attemptAdapter.currentUserId.value,
        noteOwnerName: noteOwnerName,
        initialOptionType: options.initialOptionType || "",
        isReposition: options.isReposition || false,
        isTranscriptEnabled: options.isTranscriptEnabled,
        annotationType: annotationType,
        isInNotebook,
      });
      setWithNote(false);
    });
  };

  const handleNoteChangeConfirm = async (data) => {
    const isDraft = selectedAnnotation.value.annotation.uuid === DRAFT_ANNOTATION_UUID;
    if (!isDraft && !data.isNotesDirty)
      return handlePostNoteSave({ data: selectedAnnotation.value.annotation });

    const noteContentUpdate = data.updatedNotes;
    const requestData = {
      rt_note: isDefined(noteContentUpdate) ? noteContentUpdate : noteContent.value,
      notifyStudents: data.notifyStudents,
      keywords: data.keywords,
      visibleByStudents: isDefined(data.visibleByStudents) ? data.visibleByStudents : false,
      mentionModel: data.mentionModel,
      academicYear: data.academicYear,
    };
    try {
      let response;
      if (isDraft) {
        response = await persistDraftAnnotation({
          ...selectedAnnotation.value.annotation,
          ...requestData,
        });
      } else {
        response = await updateAnnotationContent({
          uuid: selectedAnnotation.value.annotation.uuid,
          requestData,
        });
      }
      removeDirty(data.dirtyId);
      handlePostNoteSave(response);
    } catch (error) {
      onNoteSaveFail(error);
    }
  };
  const saveNote = options.handleNoteChangeConfirm || handleNoteChangeConfirm;

  const handlePostNoteSave = (response) => {
    const annotation = response.data;
    const noteOwnerFlags = getNoteOwnerFlags(annotation);
    if (annotation && annotationAnchor.isAnnotationPageDrawerExist()) {
      const anchorId =
        annotation.type === ANNOTATION_TYPE.TOOLTIP_ANNOTATION
          ? annotation.anchor
          : (annotation.data?.highlighterId ?? annotation.id);
      annotationAnchor.handleAnchor(anchorId.toString());
    }

    if (isDefined(deactiveNoteToggle) && deactiveNoteToggle) {
      deactiveNoteToggle();
    }
    AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_SAVE_SUCCESS, { annotation });
    setSelectedAnnotation({ annotation: annotation, mode: ANNOTATION_MODE.VIEW });
    mode.value = ANNOTATION_MODE.VIEW;
    contentSubpage.removeAll();
    unbindAnnotationPopupEvent();
    nextTick(() => {
      viewAnnotationNote({
        annotationUuid: annotation?.uuid,
        features: {
          comment: annotationContext?.features?.comment,
        },
        autoFocus: true,
        baseTitle: [t("common_assignment_info_title")],
        canComment: true,
        canDeleteComment: true,
        canEdit: noteOwnerFlags.canEdit,
        canDelete: noteOwnerFlags.canDelete,
        canToggleComment,
        showOverflowMenu: true,
        numberOfComments: 0,
        tooltipData: annotation?.data?.tooltipData,
        visibleByStudents: annotation?.visibleByStudents,
        getMentionableStudentList: getMentionableStudentList,
        getMentionableTeacherList: getMentionableTeacherList,
        viewedUserId: attemptAdapter.currentUserId.value,
        noteOwnerName: noteOwnerName,
        isInNotebook,
      });
    });
  };

  const handleNoteChangeCancel = () => {
    const annotation = selectedAnnotation.value.annotation;
    const noteOwnerFlags = getNoteOwnerFlags(annotation);
    const isDraft = annotation?.uuid === DRAFT_ANNOTATION_UUID;
    if (annotation && !isInNotebook) {
      const anchorId =
        annotation.type === ANNOTATION_TYPE.TOOLTIP_ANNOTATION
          ? annotation.anchor
          : (annotation.data?.highlighterId ?? annotation.id);
      annotationAnchor.handleAnchor(anchorId.toString());
    }

    if (isDefined(deactiveNoteToggle) && deactiveNoteToggle) {
      deactiveNoteToggle();
    }

    AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_POPUP_CLOSED, {});
    setSelectedAnnotation({ annotation: annotation, mode: ANNOTATION_MODE.VIEW });
    mode.value = ANNOTATION_MODE.VIEW;

    contentSubpage.removeAll();
    unbindAnnotationPopupEvent();

    if (isDraft) {
      deleteDraftAnnotation();
    } else {
      nextTick(() => {
        viewAnnotationNote({
          annotationUuid: annotation?.uuid,
          features: {
            comment: annotationContext?.features?.comment,
          },
          autoFocus: true,
          baseTitle: [t("common_assignment_info_title")],
          canComment: true,
          canDeleteComment: true,
          canEdit: noteOwnerFlags.canEdit,
          canDelete: noteOwnerFlags.canDelete,
          canToggleComment,
          showOverflowMenu: true,
          numberOfComments: 0,
          tooltipData: annotation?.data?.tooltipData,
          visibleByStudents: annotation?.visibleByStudents,
          getMentionableStudentList: getMentionableStudentList,
          getMentionableTeacherList: getMentionableTeacherList,
          viewedUserId: attemptAdapter.currentUserId.value,
          noteOwnerName: noteOwnerName,
          isInNotebook,
        });
      });
    }
  };
  const cancelSaveNote = options.handleNoteChangeCancel || handleNoteChangeCancel;

  const scrollToLastChangedAnnotation = (drawerContentType: string) => {
    annotationAnchor.handleExpandDrawer({ drawerContentType });
    if (!lastSelectedAnnotation.value) return;

    nextTick(() => {
      let selector =
        lastSelectedAnnotation.value.type === ANNOTATION_TYPE.ACTIVITY_NOTE
          ? `#activity-note-`
          : `#annotation-`;
      selector += lastSelectedAnnotation.value.id;
      const baseElement = document.querySelector(".annotation-list");
      let element: Element | undefined;

      if (baseElement) {
        element = DOMUtils.elOf(baseElement, `[id='${selector.slice(1)}']`)[0];
      }
      element?.classList.add("selected");
      annotationAnchor.handleExpandAnnotationAccordion(element, true);

      nextTick(() => {
        if (!(element instanceof HTMLElement)) return;
        if (DeviceUtils.isMobile() || DeviceUtils.isTablet()) {
          const container = document.querySelector(".page-side .page-drawer .page-drawer-content");
          let additionalOffset = element.offsetWidth / 5;
          if (DeviceUtils.isTablet()) {
            additionalOffset += ANCHOR_ADDITIONAL_OFFSET.LEFT_ACTIVITY_NAV;
          }
          ScrollUtils.scrollLeftOfContainerWithOffset(element, container, additionalOffset);
        } else {
          const container = document.querySelector(".page-side .page-drawer");
          const headerOffset = 48;
          container?.scrollTo({
            top:
              element.getBoundingClientRect().top +
              container?.scrollTop -
              headerOffset -
              annotationAnchor.getAnchorAdditionalOffset?.value,
            left: 0,
            behavior: "smooth",
          });
        }
      });
    });
  };

  const bindAnnotationPopupEvent = () => {
    unbindAnnotationPopupEvent();
    saveNoteUnsubscribe = AnnotationEventBus.$on(ANNOTATION_POPUP_EVENT.ON_NOTE_SAVE, saveNote);
    cancelNoteUnsubscribe = AnnotationEventBus.$on(
      ANNOTATION_POPUP_EVENT.ON_NOTE_CANCEL,
      cancelSaveNote,
    );

    if (saveNoteUnsubscribe) {
      eventBusWatch.trackWatch(saveNoteUnsubscribe);
    }
    if (cancelNoteUnsubscribe) {
      eventBusWatch.trackWatch(cancelNoteUnsubscribe);
    }
  };

  const unbindAnnotationPopupEvent = (): void => {
    if (saveNoteUnsubscribe) {
      saveNoteUnsubscribe();
      saveNoteUnsubscribe = null;
    }
    if (cancelNoteUnsubscribe) {
      cancelNoteUnsubscribe();
      cancelNoteUnsubscribe = null;
    }
  };

  const noteContent = computed(() => selectedAnnotation.value?.annotation?.note ?? "");
  const noteKeywords = computed(() => selectedAnnotation.value?.annotation?.keywords ?? "");
  const noteOwnerName = computed(() => {
    const annotation = selectedAnnotation.value?.annotation;
    if (!annotation) return "";
    if (annotation.appFeatureId) {
      const appFeature = getIntegratorAppAccessByAppFeatureId(annotation.appFeatureId);
      return appFeature.appFeatureName;
    }
    return annotation.ownerName || userFormattedName;
  });
  const isAlgebraKitAnnotation = computed(() => {
    const appFeature =
      selectedAnnotation.value?.annotation?.appFeatureId &&
      getIntegratorAppAccessByAppFeatureId(selectedAnnotation.value.annotation.appFeatureId);
    return appFeature?.category === INTEGRATOR_APP_FEATURE_CATEGORY.AK;
  });
  const isLfaSAorGC = computed(() => {
    const appFeature =
      selectedAnnotation.value?.annotation?.appFeatureId &&
      getIntegratorAppAccessByAppFeatureId(selectedAnnotation.value.annotation.appFeatureId);
    return (
      appFeature?.category === INTEGRATOR_APP_FEATURE_CATEGORY.GC ||
      appFeature?.category === INTEGRATOR_APP_FEATURE_CATEGORY.SA
    );
  });

  const additionalScrollingOffset = computed(() => (isOwnView.value ? 0 : 70));

  const getContext = () =>
    new Promise((resolve, reject) => {
      const annotation = selectedAnnotation.value?.annotation;
      if (annotation) {
        resolve({
          resourceType: "ANNOTATION",
          resourceUuid: annotation.uuid,
          resourceId: annotation.id,
        });
      } else {
        reject();
      }
    });

  const getRichTextContext = options.getContext || getContext;
  const richTextEditorConfig = computed(() => {
    return {
      plugins: {
        fileUpload: {
          fileServiceConfig: { getContext: getRichTextContext, type: "ugc" },
        },
        drawing: {
          fileServiceConfig: { getContext: getRichTextContext, type: "ugc" },
        },
        recording: {
          fileServiceConfig: { getContext: getRichTextContext, type: "ugc" },
        },
        customMention: {
          mentionConfig: {
            getMentionableTeacherList: getMentionableTeacherList,
            getMentionableStudentList: getMentionableStudentList,
            includeBuddyListMentions: includeBuddyListMentions.value,
          },
        },
      },
    };
  });

  const getHighlightedContent = () => {
    const data = selectedAnnotation.value?.annotation?.data;
    return data &&
      (selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.TEXT_ANNOTATION ||
        selectedAnnotation.value.annotation.type === ANNOTATION_TYPE.TIMESTAMP_ANNOTATION)
      ? data.highlightedContent
      : "";
  };

  const getHighlighterClass = () =>
    selectedAnnotation.value?.annotation?.data?.highlighterClass ?? null;

  const onNoteSaveFail = (error) =>
    AnnotationEventBus.$emit(ANNOTATION_POPUP_EVENT.ON_NOTE_SAVE_FAIL, { error });

  function populateActivityNoteData(
    noteContent: string,
    noteKeywords: string,
    options: ActivityNoteOptions = {},
  ) {
    const visibleByStudents = options.visibleByStudents || false;
    const mentionModel = options.mentionModel || null;
    const ownerId = annotationContext?.userId ?? attemptAdapter.currentUserId.value;

    return {
      resourceUuid: annotationContext?.resourceUuid,
      resourceType: annotationContext?.resourceType,
      academicYear: annotationContext?.academicYear,
      annotationResourceId: annotationContext?.pageId,
      annotationResourceType: "LESSON_ACTIVITY",
      anchor: "LESSON_ACTIVTY_" + annotationContext?.pageId,
      contextType: annotationContext?.contextType,
      groupingId: annotationContext?.pageId,
      type: ANNOTATION_TYPE.ACTIVITY_NOTE,
      rt_note: noteContent,
      keywords: noteKeywords,
      collectionOwner: annotationContext?.userId,
      allowComment: !visibleByStudents,
      ownerType: isActivityTeamResponse && isStudent ? OWNER_TYPE.TEAM : OWNER_TYPE.USER,
      ownerId,
      visibleByStudents,
      mentionModel,
      uuid: "",
    };
  }

  function populateTextAnnotationData(
    noteContent: string,
    highlightData: HighlightData,
    anchor: string,
    ownerType: string,
    annotationResourceId: number,
  ) {
    const isInAttemptAndTeacherIsAnnotatingStudentAnswer =
      annotationContext?.contextType === ANNOTATION_CONTEXT_TYPE.ATTEMPT &&
      annotationResourceId !== undefined &&
      annotationResourceId !== null &&
      !isEmpty(annotationResourceId);

    let resolvedAnnotationResourceId;
    let resolvedAnnotationResourceType;
    if (isInAttemptAndTeacherIsAnnotatingStudentAnswer) {
      resolvedAnnotationResourceId = Number(annotationResourceId);
      resolvedAnnotationResourceType = ANNOTATION_RESOURCE_TYPE.QUESTION_ANSWER;
    } else {
      resolvedAnnotationResourceType = ANNOTATION_RESOURCE_TYPE.LESSON_COMPONENT;
      // componentId may be a numeric string in attempt context; quiz anchors carry question ids,
      // not component ids, so the anchor parse is only safe as a last resort
      const contextComponentId = Number(annotationContext?.componentId);
      resolvedAnnotationResourceId =
        Number.isFinite(contextComponentId) && contextComponentId > 0
          ? contextComponentId
          : parseOwningComponentIdFromAnchor(anchor);
    }

    const ownerId = annotationContext?.userId ?? attemptAdapter.currentUserId.value;

    const position = highlightData.highlighterSelectors[0].start;

    return {
      resourceUuid: annotationContext?.resourceUuid,
      resourceType: annotationContext?.resourceType,
      academicYear: annotationContext?.academicYear,
      annotationResourceId: resolvedAnnotationResourceId,
      annotationResourceType: resolvedAnnotationResourceType,
      anchor: anchor,
      contextType: annotationContext?.contextType,
      groupingId: annotationContext?.pageId,
      type: ANNOTATION_TYPE.TEXT_ANNOTATION,
      rt_note: isEmpty(noteContent) ? "" : noteContent,
      data: highlightData,
      collectionOwner: annotationContext?.userId,
      allowComment: true,
      ownerType,
      ownerId,
      indexPosition: position,
      visibleByStudents: false,
    };
  }

  function populateAnnotationNoteContent(content: AnnotationContent): HTMLElement {
    const contentElement = document.createElement("div");
    contentElement.className = "paragraph";

    if (!isEmpty(content.pronunciation)) {
      const div = document.createElement("div");
      div.innerHTML = content.pronunciation!;
      contentElement.appendChild(div);
    }

    const ol = document.createElement("ol");
    content.definitions.forEach((definition) => {
      if (definition.definition) {
        const li = document.createElement("li");
        li.innerHTML = definition.definition;
        ol.appendChild(li);
      }
      if (definition.example) {
        const ul = document.createElement("ul");
        const li2 = document.createElement("li");
        li2.innerHTML = definition.example;
        ul.appendChild(li2);
        ol.appendChild(ul);
      }
    });

    contentElement.appendChild(ol);
    return contentElement;
  }

  function addTermToNoteContent(content: string, term: string): string {
    const termTemplate = `<div>${term}</div><br>`;
    return termTemplate + content;
  }

  return {
    noteContent,
    noteKeywords,
    noteOwnerName,
    isAlgebraKitAnnotation,
    isLfaSAorGC,
    additionalScrollingOffset,
    selectedAnnotation,
    handleEditNotes,
    handleNoteChangeConfirm,
    handleNoteChangeCancel,
    handlePostNoteSave,
    scrollToLastChangedAnnotation,
    bindAnnotationPopupEvent,
    unbindAnnotationPopupEvent,
    richTextEditorConfig,
    getHighlightedContent,
    getHighlighterClass,
    populateActivityNoteData,
    populateTextAnnotationData,
    populateAnnotationNoteContent,
    addTermToNoteContent,
    viewAnnotationNote,
    onNoteSaveFail,
  };
}
